'use client'
import { useEffect, useState } from 'react'
import { formatDate } from 'lib/utils'
import ConfirmDialog from 'popup/ConfirmDialog'
import { Save, Search } from 'lucide-react';
import { Button } from 'antd';

interface ExamResultsTabProps {
    courseId: string
    students: any[]
    examResults: any[]
    examTypes: string[]
    onSave?: (saveData: any[]) => Promise<void>
    onFetch?: () => Promise<void>
}

export default function ExamResultsTab({
                                           courseId,
                                           students,
                                           examResults,
                                           examTypes,
                                           onSave,
                                           onFetch
                                       }: ExamResultsTabProps) {
    const [filteredStudents, setFilteredStudents] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [formData, setFormData] = useState<Record<string, Record<string, string>>>({})
    const [showConfirm, setShowConfirm] = useState(false)
    const [errors, setErrors] = useState<Record<string, Record<string, string>>>({})
    const [searchTerm, setSearchTerm] = useState('')

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredStudents(students)
        } else {
            const filtered = students.filter(student =>
                student.fullName.toLowerCase().includes(searchTerm.toLowerCase()))
            setFilteredStudents(filtered)
        }
    }, [searchTerm, students])

    useEffect(() => {
        const initializeData = () => {
            setIsLoading(true)
            try {
                setErrors({})
                const initialFormData: Record<string, Record<string, string>> = {}
                const initialErrors: Record<string, Record<string, string>> = {}

                students.forEach((student) => {
                    initialFormData[student.id] = {}
                    initialErrors[student.id] = {}

                    const studentResult = examResults.find((r: any) => r.studentId === student.id)
                    if (studentResult) {
                        Object.entries(studentResult.results).forEach(([examType, exams]: [string, any]) => {
                            exams.forEach((exam: any) => {
                                initialFormData[student.id][exam.examId] = exam.result?.toString() || ''
                                initialErrors[student.id][exam.examId] = ''
                            })
                        })
                    }
                })

                setFormData(initialFormData)
                setErrors(initialErrors)
            } catch (error) {
                console.error('Failed to initialize exam results:', error)
            } finally {
                setIsLoading(false)
            }
        }

        if (students.length > 0 && examResults.length > 0) {
            initializeData()
        }
    }, [students, examResults])

    const validateInput = (value: string): string => {
        if (value === '') return ''
        const num = parseFloat(value)
        if (isNaN(num)) return 'Vui lòng nhập số'
        if (num < 0 || num > 100) return 'Giá trị phải từ 0 đến 100'
        return ''
    }

    const handleResultChange = (studentId: string, examId: string, value: string) => {
        const error = validateInput(value)

        setErrors(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                [examId]: error
            }
        }))

        setFormData(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                [examId]: value
            }
        }))
    }

    const handleSaveResults = async () => {
        try {
            let hasErrors = false
            const newErrors: Record<string, Record<string, string>> = {}

            students.forEach(student => {
                newErrors[student.id] = {}
                allExams.forEach(exam => {
                    const value = formData[student.id]?.[exam.examId] || ''
                    const error = validateInput(value)
                    newErrors[student.id][exam.examId] = error
                    if (error) hasErrors = true
                })
            })

            setErrors(newErrors)

            if (hasErrors) {
                alert('Vui lòng kiểm tra lại các giá trị nhập. Tất cả điểm số phải từ 0 đến 100.')
                return
            }

            setIsSaving(true)

            const saveData = students.map(student => {
                const studentResults: any = {}

                examResults.forEach(resultItem => {
                    if (resultItem.studentId === student.id) {
                        Object.entries(resultItem.results).forEach(([examType, exams]: [string, any]) => {
                            studentResults[examType] = exams.map((exam: any) => ({
                                examId: exam.examId,
                                examName: exam.examName,
                                result: formData[student.id]?.[exam.examId] ? parseFloat(formData[student.id][exam.examId]) : null
                            }))
                        })
                    }
                })

                return {
                    studentId: student.id,
                    studentName: student.fullName,
                    results: studentResults
                }
            })

            if (onSave) {
                await onSave(saveData)
            }

            if (onFetch) {
                await onFetch()
            }
        } catch (error) {
            console.error('Failed to save results:', error)
            alert('Lưu kết quả thi thất bại. Vui lòng thử lại.')
        } finally {
            setIsSaving(false)
            setShowConfirm(false)
        }
    }

    const allExams: any[] = [];
    const unsortedExams: any[] = [];

    examResults.forEach(result => {
        Object.values(result.results).forEach((exams: any) => {
            exams.forEach((exam: any) => {
                if (!allExams.some(e => e.examId === exam.examId) && !unsortedExams.some(e => e.examId === exam.examId)) {
                    const examTypeIndex = examTypes.indexOf(exam.examName);
                    if (examTypeIndex !== -1) {
                        allExams.push({
                            ...exam,
                            sortOrder: examTypeIndex
                        });
                    } else {
                        unsortedExams.push(exam);
                    }
                }
            });
        });
    })
    allExams.sort((a, b) => a.sortOrder - b.sortOrder);
    const sortedExams = [...allExams, ...unsortedExams];

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        )
    }

    return (
        <div className="container mx-auto p-2 max-w-full pb-16">
            <div className="items-center mb-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-2">
                    <h1 className="text-xl font-bold text-left">QUẢN LÝ KẾT QUẢ THI</h1>
                    <div className="relative w-full md:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Tìm kiếm học viên..."
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white overflow-hidden ">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10">STT</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">Họ tên</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Ngày sinh</th>

                            {sortedExams.map(exam => (
                                <th key={exam.examId} className="px-3 py-2 text-left text-xs font-medium text-gray-500 tracking-wider min-w-[100px]">
                                    <div className="truncate max-w-[100px]">{exam.examName}</div>
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {filteredStudents.map((student, index) => (
                            <tr key={student.id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 whitespace-nowrap text-center text-sm text-gray-900">{index + 1}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                    <div className="">{student.fullName}</div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{formatDate(student.birthDate)}</td>

                                {sortedExams.map(exam => {
                                    const resultValue = formData[student.id]?.[exam.examId] || ''
                                    const error = errors[student.id]?.[exam.examId] || ''
                                    return (
                                        <td key={`${student.id}-${exam.examId}`} className="px-3 py-2 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={resultValue}
                                                    onChange={(e) => handleResultChange(student.id, exam.examId, e.target.value)}
                                                    className={`w-16 px-2 py-1 border rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm ${
                                                        error ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                    }`}
                                                />
                                                {error && (
                                                    <span className="text-red-500 text-xs mt-0.5">{error}</span>
                                                )}
                                            </div>
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="fixed bottom-4 right-4 z-50">
                <Button
                    onClick={() => setShowConfirm(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                    disabled={isSaving}
                >
                    <Save className="mr-2" />
                    {isSaving ? 'Đang lưu...' : 'Lưu kết quả'}
                </Button>
            </div>

            <ConfirmDialog
                isOpen={showConfirm}
                title="Xác nhận lưu kết quả thi"
                description="Bạn có chắc chắn muốn lưu các kết quả thi này?"
                onClose={() => setShowConfirm(false)}
                onConfirm={handleSaveResults}
            />
        </div>
    )
}